import { profileApi } from '@/features/profile-management';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { Ban, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface BlockMemberButtonProps {
  nodeId: number;
  username: string;
  /** Called once blocked: the member's profile and conversations are now hidden. */
  onBlocked: () => void;
  /** Icon-only button (chat header) instead of the full-width one. */
  compact?: boolean;
}

/**
 * "Bloquer ce membre" with confirmation. A block hides both members from each
 * other (search, lists, profile, conversations) until the blocker lifts it
 * from the settings.
 */
export function BlockMemberButton({
  nodeId,
  username,
  onBlocked,
  compact = false,
}: BlockMemberButtonProps) {
  const { t } = useTranslate();
  const queryClient = useQueryClient();

  const blockMutation = useMutation({
    mutationFn: () => profileApi.blockMember(nodeId),
    onSuccess: () => {
      toast.success(t('block.blocked', { username }));
      // Every list that showed the member is now stale
      for (const queryKey of [
        ['profiles'],
        ['converses'],
        ['interactionProfiles'],
        ['matches'],
        ['blockedMembers'],
      ]) {
        queryClient.invalidateQueries({ queryKey });
      }
      onBlocked();
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {compact ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('block.button')}
            title={t('block.button')}
            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          >
            <Ban className="h-5 w-5" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="w-full text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          >
            <Ban className="mr-2 h-4 w-4" />
            {t('block.button')}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">
            {t('block.confirmTitle', { username })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('block.confirmDescription')}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => blockMutation.mutate()}
            disabled={blockMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {blockMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('block.short')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
